/**
 * Script Action Utilities
 * Works with DynamoDB as single source of truth
 * No hard-coded fallbacks - all data comes from database
 */

/**
 * Get script path for a given action
 * @param {string} actionKey - The action key
 * @param {object} actionData - The action object from DynamoDB
 * @param {string} userInput - User-provided script path (for user-input actions)
 * @returns {string} The script path to use
 */
export const getScriptPath = (actionKey, actionData, userInput = "") => {
  if (actionKey === "None") {
    return "";
  }

  if (!actionData) {
    return "";
  }

  // If action requires user input, use provided value
  if (actionData.needsUserInput) {
    return userInput;
  }

  // Otherwise use script_path from DynamoDB
  return actionData.script_path || "";
};

/**
 * Get action status for database storage
 * @param {string} actionKey - The action key
 * @returns {boolean} true if action should be active, false if inactive
 */
export const getActionStatus = (actionKey) => {
  return actionKey !== "None";
};

/**
 * Find action key by script path
 * @param {string} scriptPath - The script path to search for
 * @param {object} scriptActions - The SCRIPT_ACTIONS object from DynamoDB
 * @returns {string} The action key, or "None" if not found
 */
export const findActionByScriptPath = (scriptPath, scriptActions) => {
  if (!scriptPath || !scriptActions) {
    return "None";
  }

  // Search through all actions for matching script_path
  for (const [key, value] of Object.entries(scriptActions)) {
    if (value.script_path === scriptPath) {
      return key;
    }
  }

  // If no predefined action matches, return "Scripts" (user-input action)
  // This handles custom scripts
  return "Scripts";
};

/**
 * Transform DynamoDB items to SCRIPT_ACTIONS format
 * @param {array} items - Items from DynamoDB listScriptPathMappings query
 * @returns {object} Formatted SCRIPT_ACTIONS object
 */
export const transformDynamoDBToActions = (items) => {
  if (!items || !Array.isArray(items)) {
    return {};
  }

  const actions = {};

  items.forEach(item => {
    actions[item.action_key] = {
      action_key: item.action_key,
      script_path: item.script_path || "",
      description: item.description || item.action_key,
      needsUserInput: item.needsUserInput || false
    };
  });

  return actions;
};
